// src/features/alerts/CriticalAlertBanner.jsx
// Full-width banner shown when extreme or severe NWS alert is active.
// Visually dominates — impossible to miss.
// Dismissible per session, reappears if alert changes.

import { useState, useEffect } from 'react';
import { useAlerts } from '@hooks/useAlerts';
import { T } from '@components/theme';

const SEV_STYLE = {
  extreme: { bg: '#7c3aed', border: '#9333ea', pulse: true,  label: 'EXTREME' },
  severe:  { bg: '#dc2626', border: '#ef4444', pulse: false, label: 'SEVERE'  },
};

export function CriticalAlertBanner() {
  const { extremeAlert } = useAlerts();
  const [dismissedId, setDismissedId] = useState(null);
  const [visible, setVisible] = useState(true);

  // Re-show if alert changes
  useEffect(() => {
    const id = extremeAlert?.id ?? extremeAlert?.title;
    if (id && id !== dismissedId) setVisible(true);
  }, [extremeAlert?.id, extremeAlert?.title]);

  if (!extremeAlert || !visible) return null;

  const sev = SEV_STYLE[extremeAlert.severity] ?? SEV_STYLE.severe;
  const eventName = extremeAlert.event ?? extremeAlert.title ?? 'Severe Weather Alert';
  const area = extremeAlert.area ?? '';

  return (
    <div style={{
      background: sev.bg,
      borderBottom: `2px solid ${sev.border}`,
      flexShrink: 0,
      animation: sev.pulse ? 'none' : 'none',
    }}>
      <div style={{
        padding: '8px 14px',
        display: 'flex', alignItems: 'center', gap: 10,
      }}>
        {/* Severity badge */}
        <div style={{
          fontSize: 8, fontWeight: 900, color: '#fff',
          background: 'rgba(0,0,0,0.25)',
          borderRadius: 4, padding: '2px 6px',
          letterSpacing: 1, flexShrink: 0,
        }}>
          {sev.label}
        </div>

        {/* Alert content */}
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{
            fontSize: 12, fontWeight: 900, color: '#fff',
            lineHeight: 1.3,
            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
          }}>
            {eventName}
          </div>
          {area && (
            <div style={{
              fontSize: 9, color: 'rgba(255,255,255,0.75)',
              marginTop: 1, lineHeight: 1.3,
              overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
            }}>
              {area.split(';').slice(0, 3).join(' · ')}
              {area.split(';').length > 3 ? ` +${area.split(';').length - 3} more` : ''}
            </div>
          )}
        </div>

        {/* Dismiss */}
        <button
          onClick={() => {
            setDismissedId(extremeAlert.id ?? extremeAlert.title);
            setVisible(false);
          }}
          style={{
            background: 'rgba(0,0,0,0.2)', border: 'none',
            borderRadius: 4, padding: '3px 7px',
            color: '#fff', fontSize: 11, cursor: 'pointer',
            fontFamily: T.font, flexShrink: 0, opacity: 0.8,
          }}
          aria-label="Dismiss alert banner"
        >
          ×
        </button>
      </div>
    </div>
  );
}
